import Head from 'next/head';
import { NextSeo } from 'next-seo';
import { Content } from '@/screens/Content';
import styled from 'styled-components';
import { screen } from '@/helpers/screen';

//vendor
import ReactPlayer from 'react-player/lazy';
import { managedTransOptions } from '@/vendor/HubspotFormIntegration';

//style
const VideoWrapper = styled.div`
  margin: 0 auto;
  height: 138px;
  width: 100%;
  position: relative;

  ${screen.md} {
    align-self: center;
    height: 346px;
  }

  ${screen.lg} {
    height: 470px;
  }

  ${screen.xxlg} {
    height: 670px;
  }
`;

// data
const heroImages = {
  xxlarge: 'images/content/hero/hero-loadsmart-01-xxlg@2x.jpg',
  large: 'images/content/hero/hero-loadsmart-01-lg@2x.jpg',
  medium: 'images/content/hero/hero-loadsmart-01-md@2x.jpg',
  small: 'images/content/hero/hero-loadsmart-01-sm@2x.jpg',
};

const contentCopy = {
  date: null,
  title: `Managed Transportation – What it is and Why You Need It`,
  slug: `what-is-managed-transportation-`,
  highlight: (
    <>
      <VideoWrapper>
        <ReactPlayer
          className="react-player"
          url="www.youtube.com/watch?v=Xlf9Umf3jC4?rel=0"
          controls
          width="100%"
          height="100%"
        />
      </VideoWrapper>

      <p>
        It’s essential to understand the benefits of partnering with a Managed
        Transportation Services provider.
      </p>
      <p>
        Many shippers turn to Managed Transportation solutions to outsource
        their logistics activities and partner with experts to navigate the
        continuously changing market. Outsourcing is a great option for shippers
        looking to increase their flexibility and scale quickly without the
        burden of high fixed costs.
      </p>
      <p>
        As shippers look to fortify their supply chain’s and implement
        forward-looking network strategies, partnering with a Managed
        Transportation provider not only introduces an agile operating framework
        but impacts the bottom line.
      </p>
      <p>
        So for those not already in the know, what is Managed Transportation all
        about and how can it help you plan for the unexpected?
      </p>
    </>
  ),
  copy: (
    <>
      <nav>
        <ul>
          <li>
            <a href="#what-is-managed-transportation">
              What is Managed Transportation?
            </a>
          </li>
          <li>
            <a href="#the-benefits-of-managed-transportation-services">
              The Benefits of Managed Transportation Services
            </a>
          </li>
          <li>
            <a href="#choosing-the-right-managed-transportation-company">
              Choosing the Right Managed Transportation Company
            </a>
          </li>
        </ul>
      </nav>
      <h3 id="what-is-managed-transportation">
        What is Managed Transportation?
      </h3>
      <p>
        Managed Transportation refers to a third-party logistics (3PL) service
        that provides end-to-end management of a company’s supply chain and
        transportation operations. These services typically include a dedicated
        operations team that completes the procurement, planning, optimization,
        execution, and reporting on the movement of goods across a shipper’s
        network of transportation. In addition to increasing flexibility, the
        goal of a Managed Transportation partnership is to increase operational
        efficiency, reduce costs, and introduce a continuous improvement
        framework that evolves with the needs of both the market and shipper.
      </p>
      <p>
        In a Managed Transportation partnership, the provider will execute all
        day-to-day tasks on behalf of the shipper, which typically include
        routing guide management, shipment tendering & tracking, and exception
        management, etc. By removing these from a shipper’s daily
        responsibilities, they are able to focus their time and resources on
        other aspects of their business.
      </p>
      <p>
        Managed Transportation services are flexible in the sense that they
        allow you to collaborate and design a plan that works best for your
        business. Some additional services that a Managed Transportation
        solution will provide, include:
        <ul>
          <li>Market Forecasting & Actionable Insights</li>
          <li>Bid Management & Procurement Services</li>
          <li>Order Optimization & Consolidation</li>
          <li>Mode Optimization & Contract/Spot Lane Selection</li>
          <li>End-to-End Shipment Execution & Management</li>
          <li>Freight Audit & Payment</li>
          <li>KPI Management</li>
          <li>Reporting & Analytics</li>
          <li>Continuous Improvement</li>
          <li>Administrative Management & Carrier Oversight</li>
        </ul>
      </p>

      <h3 id="the-benefits-of-managed-transportation-services">
        The Benefits of Managed Transportation Services
      </h3>
      <p>
        Partnering with a provider that will be your single point of contact for
        many, if not all, of your logistics needs can come with many benefits.
        Typically, a shipper will have a small internal team that works closely
        with a dedicated team from the Managed Transportation provider.
      </p>
      <p>
        The Managed Transportation team will then take care of the day-to-day
        operations, carrier management, and ongoing network measurement &
        analysis, allowing the shipper’s team to have the time needed to focus
        on their big picture transportation strategy.
      </p>
      <p>
        A few additional benefits of outsourcing to a Managed Transportation
        partner:
      </p>
      <h2>More Time to Spend on Your Business</h2>
      <p>
        Shippers tend to wear a lot of hats. And whatever hat they’re wearing,
        it’s taking time away from time spent on growing their business.
        Partnering with a Managed Transportation provider earns back some of
        that time. And it doesn’t just save time; it can ultimately save money.
      </p>
      <p>
        Imagine the time spent sourcing the best rate to get your product from A
        to B. Having a carrier network already in place that can provide you
        with capacity at a great rate, and someone else finding the capacity for
        you, may be a simple way to improve your ROI.
      </p>
      <h2>More Money to Invest in Your Business</h2>
      <p>
        If time is money, saving time means saving money that could be invested
        in your business. Since your transportation services will be managed
        more efficiently, this should translate into cost savings. Many benefits
        come from taking advantage of your provider’s carrier network and other
        services.
      </p>
      <p>
        For example, with having a Managed Transportation partner you should
        receive increased access to capacity - providers can source capacity
        that might not be available to you otherwise. Providers have developed
        relationships with many quality carriers and are typically able to
        negotiate better on your behalf.
      </p>
      <h2>Access to Supply Chain Transportation Technology</h2>
      <p>
        Many MT providers can give you access to a platform where you are able
        to plan and execute the entire shipping process, including proper
        documentation and ensuring that the shipment is compliant. With the
        additional technology added to your operations, you already have all of
        your data in one place. This means your Managed Transportation provider
        is better positioned to analyze and produce advanced reporting on your
        shipments to create a customized, and effective shipping strategy.
      </p>
      <p>
        Now that you understand what managed transportation is and the
        associated benefits, how do you go about choosing the right partner?
      </p>

      <h3 id="choosing-the-right-managed-transportation-company">
        Choosing the Right Managed Transportation Company
      </h3>
      <p>
        The pandemic and post-COVID era have introduced some new challenges to
        transportation management. Challenges that could indirectly impact
        shippers and make it all the more important to work with a Managed
        Transportation provider.
      </p>
      <p>
        Here are a few things to look for when looking at when considering
        different companies.
      </p>
      <p>
        First, make a list that includes what you need from a service provider.
        And of the services and benefits mentioned above, which one is the most
        important to you? What’s your budget? This information will help narrow
        your search.
      </p>
      <p>
        A few other things to consider include the following:
        <ul>
          <li>Your freight volume and frequency by lanes</li>
          <li>Most common shipping modes</li>
          <li>Special delivery and product handling requirements</li>
          <li>
            If you plan to scale, determine if the provider is in a position to
            scale with you
          </li>
        </ul>
      </p>
      <p>
        Finally, check their references to ensure they have the ability to do
        what they say they can do. Keep in mind that your customer’s
        expectations are high. Make sure your partner is capable of fulfilling
        those expectations.
      </p>
    </>
  ),
};

const sidebarLinks = [
  {
    label: 'Why Startup Manufacturers Should Delegate Out Their Logistics.',
    url: 'https://blog.loadsmart.com/startup-manufacturers-managed-transportation',
    external: true,
  },
  {
    label:
      'Drought, Wars, and Pirates! Volatility in the supply chain means trouble for logistics teams.',
    url: 'https://blog.loadsmart.com/drought-wars-and-pirates-volatility-in-the-supply-chain-means-trouble-for-logistics-teams',
    external: true,
  },
  {
    label: 'Surviving the Driver Shortage and Supply Chain Talent Gap.',
    url: 'https://blog.loadsmart.com/surviving-the-driver-shortage-and-supply-chain-talent-gap',
    external: true,
  },
  {
    label:
      'Loadsmart’s Look Ahead: An Analysis of Key Freight & Economic Indicators to Watch.',
    url: 'https://lp.loadsmart.com/en/monthly-analysis-freight-economic-indicators',
    external: true,
  },
];

const bannerContent = null;

const formContent = {
  title: (
    <>
      Leverage a dedicated team of seasoned logistics experts to help find
      savings, build a custom solution, and execute end-to-end logistics on your
      behalf.
    </>
  ),
  subtitle:
    'If spreadsheets and tribal knowledge are acting as load-bearing pillars in your operation, it’s time to find a better way. Get the full benefit of a dedicated team of seasoned logistics experts with the technology to match when you choose Loadsmart Managed Transportation.',
  image: {
    small: '/images/content/mtbanner/managed-transportation-banner.png',
    medium: '/images/content/mtbanner/managed-transportation-banner.png',
    large: '/images/content/mtbanner/managed-transportation-banner.png',
    xxlarge: '/images/content/mtbanner/managed-transportation-banner.png',
  },
  alt: '$40 Million in Freight over a spreadsheet icon',
  form: managedTransOptions,
};

export default function WhatIsTruckManagementSoftware() {
  return (
    <>
      <Head>
        <meta
          type="keywords"
          content="managed transportation, managed transportation services, managed transportation providers, freight management firms, logistics consulting, freight and logistics consulting, freight management services, freight management consulting, logistics business consulting, freight management companies"
        />
      </Head>
      <NextSeo
        nofollow={process.env.NOINDEX_FLAG}
        noindex={process.env.NOINDEX_FLAG}
        title="Loadsmart | What is Managed Transportation | ShipperGuide TMS"
        description="Learn how managed transportation provides professional services for logistics and freight needs and helps businesses streamline their freight operations."
        canonical="https://loadsmart.com/content/what-is-managed-transportation/"
        openGraph={{
          url: 'https://loadsmart.com/content/what-is-managed-transportation/',
        }}
      />
      <Content
        hero={heroImages}
        content={contentCopy}
        sidebar={sidebarLinks}
        banner={bannerContent}
        form={formContent}
      />
    </>
  );
}
